import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '../lib/api'

/**
 * Konteks sesi yang dipakai bersama orisini.com dan seluruh SPA produk.
 *
 * `login`/`register` ada di sini walau dalam praktiknya hanya dipakai halaman
 * auth milik orisini.com. SPA produk yang kehabisan sesi mengarahkan pengguna
 * ke `loginUrl()` alih-alih menampilkan form sendiri — bukan karena
 * endpoint-nya tidak jalan dari sana (ia diteruskan ke Worker identitas dan
 * cookie tetap dipasang di domain induk), tapi supaya hanya ada SATU halaman
 * masuk yang perlu dirawat.
 *
 * Kedua sisi memanggil endpoint yang SAMA-ORIGIN (`/api/me`, `/api/auth/*`).
 * Worker produk meneruskannya ke Worker identitas lewat service binding, jadi
 * browser tidak pernah melakukan permintaan lintas-origin berkredensial dan
 * tidak ada CORS yang perlu dilonggarkan.
 */

export interface ProductAccess {
  code: string
  name: string
  /** Asal Worker produk, mis. https://pinboss.orisini.com */
  origin: string
  planCode: string
  planName: string
  status: string
  /** Langganan masih berlaku untuk dispatch/penjadwalan? */
  active: boolean
  periodEnd: number | null
  /** Sudah meminta turun ke Free; berlaku saat periodEnd lewat. */
  cancelAtPeriodEnd?: boolean
}

export interface MeResponse {
  user: {
    id: string
    email: string
    name: string | null
    role: 'user' | 'admin'
    timezone: string
    emailVerified: boolean
    createdAt: number
  }
  /** Semua produk yang terbuka untuk user ini. Sumbernya selalu orisini. */
  products: ProductAccess[]
  /**
   * Paket & pemakaian untuk produk yang SEDANG dibuka. Diisi Worker produk;
   * di orisini.com keduanya undefined karena tidak ada satu produk aktif.
   */
  plan?: {
    code: string
    name: string
    status: string
    active: boolean
    periodEnd: number | null
    cancelAtPeriodEnd?: boolean
    limits: {
      maxAccounts: number
      maxPostsPerMonth: number
      maxDevices: number
    }
  }
  /** Kuota posting bulan ini — total di SEMUA platform, bukan produk ini saja. */
  usage?: {
    periodYm: string
    posted: number
    scheduled: number
    used: number
    limit?: number
    remaining: number
    byProduct?: Record<string, number>
  }
}

interface AuthState {
  status: 'loading' | 'authenticated' | 'anonymous'
  me: MeResponse | null
  refresh: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (input: {
    email: string
    password: string
    name?: string
    timezone?: string
    acceptTos: boolean
  }) => Promise<void>
  logout: () => Promise<void>
  /** URL halaman masuk di orisini.com, lengkap dengan tujuan kembali. */
  loginUrl: (next?: string) => string
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({
  children,
  idOrigin,
}: {
  children: React.ReactNode
  /**
   * Asal Worker identitas. Di SPA orisini.com sendiri isinya string kosong
   * (sama-origin); di SPA produk isinya "https://orisini.com".
   */
  idOrigin?: string
}) {
  const [status, setStatus] = useState<AuthState['status']>('loading')
  const [me, setMe] = useState<MeResponse | null>(null)

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<MeResponse>('/me')
      setMe(data)
      setStatus('authenticated')
    } catch (err) {
      // 401 itu keadaan normal (belum login), bukan kondisi error.
      if (err instanceof ApiError && err.status === 401) {
        setMe(null)
        setStatus('anonymous')
        return
      }
      throw err
    }
  }, [])

  useEffect(() => {
    refresh().catch(() => setStatus('anonymous'))
  }, [refresh])

  const login = useCallback(
    async (email: string, password: string) => {
      await api.post('/auth/login', { email, password })
      await refresh()
    },
    [refresh],
  )

  const register = useCallback<AuthState['register']>(
    async (input) => {
      await api.post('/auth/register', {
        ...input,
        timezone: input.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      await refresh()
    },
    [refresh],
  )

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined)
    setMe(null)
    setStatus('anonymous')
  }, [])

  const loginUrl = useCallback(
    (next?: string) => {
      const target = next ?? window.location.href
      return `${idOrigin ?? ''}/login?next=${encodeURIComponent(target)}`
    },
    [idOrigin],
  )

  const value = useMemo<AuthState>(
    () => ({ status, me, refresh, login, register, logout, loginUrl }),
    [status, me, refresh, login, register, logout, loginUrl],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth harus dipakai di dalam <AuthProvider>')
  return ctx
}

/** `me` dengan `plan` dan `usage` yang dijamin ada. */
export type ProductMe = MeResponse & {
  plan: NonNullable<MeResponse['plan']>
  usage: NonNullable<MeResponse['usage']>
}

/**
 * Versi useAuth untuk SPA PRODUK.
 *
 * `plan` dan `usage` opsional di tipe bersama karena orisini.com tidak punya
 * satu produk yang sedang aktif. Di dalam SPA produk keduanya selalu diisi —
 * `GET /api/me` milik produk menggabungkannya sebelum membalas — jadi hook ini
 * menyempitkan tipenya sekali di sini, bukan dengan `?.` yang berserakan di
 * setiap halaman.
 */
export function useProductAuth(): Omit<AuthState, 'me'> & { me: ProductMe | null } {
  const ctx = useAuth()
  return { ...ctx, me: ctx.me as ProductMe | null }
}
